import type { ViewMode, Profile } from '@/types'
import { useTranslation } from 'react-i18next'
import { VIEW_MODES } from '../constants'

interface BookListHeaderProps {
    // Context
    isSharedView: boolean
    isBiblioverso: boolean
    sharedProfile: Profile | null
    onBack: () => void

    // Stats
    filteredCount: number
    totalCount: number

    // Search
    searchTerm: string
    setSearchTerm: (term: string) => void

    // View mode
    viewMode: ViewMode
    setViewMode: (mode: ViewMode) => void

    // Filters
    showFilters: boolean
    setShowFilters: (show: boolean) => void
    hasActiveFilters: boolean

    // Actions
    onAddBook: () => void
}

export default function BookListHeader({
    // Context
    isSharedView,
    isBiblioverso,
    sharedProfile,
    onBack,

    // Stats
    filteredCount,
    totalCount,

    // Search
    searchTerm,
    setSearchTerm,

    // View mode
    viewMode,
    setViewMode,

    // Filters
    showFilters,
    setShowFilters,
    hasActiveFilters,

    // Actions
    onAddBook,
}: Readonly<BookListHeaderProps>) {
    const { t } = useTranslation()

    return (
        <div className="booklist-header animate-fade-in-up">
            <div className="header-content">
                <h2 className="booklist-title">
                    {isSharedView && (
                        <button onClick={onBack} className="back-btn-inline" title={t('bookList.header.backToBiblionautas')}>
                            ⬅
                        </button>
                    )}
                    <span className="title-icon">{isSharedView ? '👀' : '📚'}</span>
                    {(() => {
                        if (isBiblioverso) return t('bookList.header.biblioverso')
                        if (isSharedView)
                            return t('bookList.header.sharedLibrary', { name: sharedProfile?.full_name || sharedProfile?.email || '...' })
                        return t('bookList.header.myLibrary')
                    })()}
                </h2>
                <div className="books-stats">
                    <span className="stat-badge">
                        <span className="stat-number">{filteredCount}</span>
                        <span className="stat-label">
                            {t('bookList.header.stats.book', { count: filteredCount })}
                            {filteredCount !== totalCount && t('bookList.header.stats.ofTotal', { total: totalCount })}
                        </span>
                    </span>
                </div>
            </div>

            {/* Header Toolbar: View Modes + Actions */}
            <div className="header-toolbar animate-fade-in-up stagger-1">
                <div className="view-mode-toggle">
                    {VIEW_MODES.map((mode) => (
                        <button
                            key={mode.id}
                            className={`view-mode-btn ${viewMode === mode.id ? 'active' : ''}`}
                            onClick={() => setViewMode(mode.id as ViewMode)}
                            title={t(mode.label)}
                        >
                            <span>{mode.icon}</span>
                        </button>
                    ))}
                </div>

                {!isBiblioverso && !isSharedView && (
                    <button className="btn-primary btn-add-book" onClick={onAddBook} title={t('bookList.header.addBook')} data-testid="add-book-button">
                        <span className="add-icon">+</span>
                        <span className="add-text">{t('bookList.header.addBook')}</span>
                    </button>
                )}
            </div>

            {/* Search Bar */}
            <div className="search-wrapper animate-fade-in-up stagger-1">
                <span className="search-icon">🔍</span>
                <input
                    type="text"
                    placeholder={t('bookList.header.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                {searchTerm && (
                    <button className="search-clear" onClick={() => setSearchTerm('')} aria-label={t('bookList.header.clearSearch')}>
                        ✕
                    </button>
                )}
                <button
                    className={`filter-toggle ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setShowFilters(!showFilters)
                    }}
                    aria-label={t('bookList.header.toggleFilters')}
                    type="button"
                >
                    <span>🎛️</span>
                    {hasActiveFilters && <span className="filter-dot"></span>}
                </button>
            </div>
        </div>
    )
}
